import React, { useCallback, useMemo, useState } from 'react';
import { X, FileSpreadsheet, Check } from 'lucide-react';
import { colors } from '../../config/colors';
import { Button } from '../UI/button';
import { useLocale } from '../../contexts/LocaleContext';
import { supplyChainBatchService } from '../../services/supplyChainBatchService';
import { cn } from '../../lib/utils';
import CsvDropZone, { spreadsheetValidator } from '../TenantCompany/CsvDropZone';
import CsvColumnConnector from '../TenantCompany/CsvColumnConnector';
import {
  BATCH_CHUNK_SIZE,
  BATCH_IMPORT_FIELD_IDS,
  buildBatchPayload,
  guessColumnMapping,
  isMappingValid,
  parseSpreadsheetBuffer,
  readFileAsArrayBuffer,
} from '../../utils/spreadsheetCompanyImport';

const FIELD_MESSAGE_KEYS = {
  ticker: 'batchImportFieldTicker',
  name: 'batchImportFieldName',
  description: 'batchImportFieldDescription',
  industry: 'batchImportFieldIndustry',
  tier: 'batchImportFieldTier',
  legalName: 'batchImportFieldLegalName',
  dunsNumber: 'batchImportFieldDunsNumber',
  region: 'batchImportFieldRegion',
  categories: 'batchImportFieldCategories',
  annualSpend: 'batchImportFieldAnnualSpend',
  criticalityScore: 'batchImportFieldCriticalityScore',
  isSoleSource: 'batchImportFieldIsSoleSource',
  isStrategic: 'batchImportFieldIsStrategic',
  uflpaFlag: 'batchImportFieldUflpaFlag',
  country: 'batchImportFieldCountry',
  sector: 'batchImportFieldSector',
  website: 'batchImportFieldWebsite',
  marketCap: 'batchImportFieldMarketCap',
  employeeCount: 'batchImportFieldEmployeeCount',
  keywords: 'batchImportFieldKeywords',
  supplyChainRisks: 'batchImportFieldSupplyChainRisks',
  riskCategories: 'batchImportFieldRiskCategories',
};

const REQUIRED = new Set(['ticker', 'name']);

const SPREADSHEET_ACCEPT =
  '.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv';

function BatchImportStepIndicator({ step, t }) {
  const steps = [
    { id: 'upload', label: t('batchImportStepUploadLabel') },
    { id: 'map', label: t('batchImportStepMapLabel') },
  ];
  const activeIndex = step === 'upload' ? 0 : 1;

  return (
    <ol className="flex items-center gap-2" aria-label={t('batchImportTitle')}>
      {steps.map((item, index) => {
        const isComplete = index < activeIndex;
        const isCurrent = index === activeIndex;
        return (
          <li key={item.id} className="flex items-center gap-2">
            {index > 0 ? (
              <span
                className={cn('h-0.5 w-6', isComplete ? 'bg-success-400' : 'bg-border-light')}
                aria-hidden
              />
            ) : null}
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
                isComplete && 'bg-success-100 text-success-700',
                isCurrent && 'bg-primary-100 text-primary-700',
                !isComplete && !isCurrent && 'bg-background-tertiary text-text-tertiary'
              )}
            >
              <span
                className={cn(
                  'flex size-5 items-center justify-center rounded-full text-[10px] font-semibold',
                  isComplete && 'bg-success-500 text-white',
                  isCurrent && 'bg-primary-500 text-primary-900',
                  !isComplete && !isCurrent && 'bg-background-card text-text-tertiary'
                )}
                aria-hidden
              >
                {isComplete ? <Check className="h-3 w-3" /> : index + 1}
              </span>
              {item.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function BatchImportCompaniesModal({ onClose, onImported }) {
  const { t } = useLocale();
  const [step, setStep] = useState('upload');
  const [headerOrientation, setHeaderOrientation] = useState('row');
  const [parseError, setParseError] = useState(null);
  const [fileBuffer, setFileBuffer] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [fileLabel, setFileLabel] = useState('');
  const [mapping, setMapping] = useState(() => {
    const init = {};
    BATCH_IMPORT_FIELD_IDS.forEach((id) => {
      init[id] = null;
    });
    return init;
  });
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const fieldLabels = useMemo(() => {
    const labels = {};
    BATCH_IMPORT_FIELD_IDS.forEach((id) => {
      labels[id] = t(FIELD_MESSAGE_KEYS[id]);
    });
    return labels;
  }, [t]);

  const resetMapping = useCallback(() => {
    const init = {};
    BATCH_IMPORT_FIELD_IDS.forEach((id) => {
      init[id] = null;
    });
    setMapping(init);
  }, []);

  const parseWithOrientation = useCallback(
    (buffer, orientation) => {
      const parsed = parseSpreadsheetBuffer(buffer, orientation);
      if (!parsed.headers.length) {
        setParseError(t('batchImportEmptyFile'));
        setHeaders([]);
        setRows([]);
        resetMapping();
        return false;
      }
      setParseError(null);
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setMapping(guessColumnMapping(parsed.headers));
      return true;
    },
    [resetMapping, t]
  );

  const processFile = async (file) => {
    if (!file) return;
    setParseError(null);
    setResult(null);
    setFileLabel(file.name);
    setHeaderOrientation('row');
    try {
      const buffer = await readFileAsArrayBuffer(file);
      setFileBuffer(buffer);
      const parsedOk = parseWithOrientation(buffer, 'row');
      if (!parsedOk) {
        setStep('upload');
        return;
      }
      setStep('map');
    } catch (_) {
      setParseError(t('batchImportParseError'));
      setStep('upload');
      setFileBuffer(null);
      setHeaders([]);
      setRows([]);
      resetMapping();
    }
  };

  const handleOrientationChange = (nextOrientation) => {
    setHeaderOrientation(nextOrientation);
    if (!fileBuffer) return;
    parseWithOrientation(fileBuffer, nextOrientation);
  };

  const handleChangeFile = () => {
    setStep('upload');
    setFileBuffer(null);
    setFileLabel('');
    setHeaders([]);
    setRows([]);
    resetMapping();
    setParseError(null);
  };

  const previewCompanies = useMemo(() => {
    if (!isMappingValid(mapping) || !rows.length) return [];
    return buildBatchPayload(rows, mapping).slice(0, 5);
  }, [rows, mapping]);

  const fullPayload = useMemo(() => {
    if (!isMappingValid(mapping)) return [];
    return buildBatchPayload(rows, mapping);
  }, [rows, mapping]);

  const chunkCount = Math.max(1, Math.ceil(fullPayload.length / BATCH_CHUNK_SIZE));

  const handleImport = async () => {
    if (!isMappingValid(mapping) || !fullPayload.length) return;
    setImporting(true);
    setResult(null);
    try {
      const res = await supplyChainBatchService.batchSuppliers(fullPayload);
      if (!res.success) {
        setResult({
          ok: false,
          message: res.message || t('batchImportFailed'),
          created: res.data?.created ?? 0,
          updated: res.data?.updated ?? 0,
          failed: res.data?.failed ?? 0,
          errors: Array.isArray(res.data?.errors) ? res.data.errors : [],
        });
        return;
      }

      const data = res.data || {};
      setResult({
        ok: true,
        created: data.created ?? 0,
        updated: data.updated ?? 0,
        failed: data.failed ?? 0,
        errors: Array.isArray(data.errors) ? data.errors : [],
        totalRequested: data.totalRows ?? fullPayload.length,
      });
      if (onImported) onImported();
    } catch (err) {
      setResult({
        ok: false,
        message: err.message || t('batchImportFailed'),
        created: 0,
        updated: 0,
        failed: fullPayload.length,
        errors: [],
      });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (!importing) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 transition-opacity"
          style={{ backgroundColor: `${colors.gray[900]}80` }}
          onClick={handleClose}
          aria-hidden="true"
        />
        <div
          className="relative w-full max-w-4xl rounded-lg shadow-2xl max-h-[90vh] overflow-y-auto"
          style={{
            borderTop: `4px solid ${colors.primary[500]}`,
            backgroundColor: colors.background.secondary,
          }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="batch-import-title"
        >
          <div
            className="sticky top-0 z-10 flex flex-col gap-4 border-b px-6 py-4"
            style={{ borderColor: colors.border.light, backgroundColor: colors.background.secondary }}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                  style={{ backgroundColor: colors.primary[100] }}
                >
                  <FileSpreadsheet className="h-5 w-5" style={{ color: colors.primary[600] }} />
                </div>
                <div className="min-w-0">
                  <h2
                    id="batch-import-title"
                    className="text-xl font-bold truncate"
                    style={{ color: colors.text.primary }}
                  >
                    {t('batchImportTitle')}
                  </h2>
                  <p className="text-sm" style={{ color: colors.text.secondary }}>
                    {step === 'upload' ? t('batchImportStepUpload') : t('batchImportStepMap')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleClose}
                disabled={importing}
                className="shrink-0 rounded-lg p-2 transition-colors"
                style={{ color: colors.text.tertiary }}
                aria-label={t('batchImportClose')}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <BatchImportStepIndicator step={step} t={t} />
          </div>

          <div className="space-y-6 p-6">
            {step === 'upload' && (
              <div className="space-y-4">
                <CsvDropZone
                  disabled={importing}
                  fileName={fileLabel || null}
                  accept={SPREADSHEET_ACCEPT}
                  validateFile={spreadsheetValidator}
                  titleKey="batchImportDropTitle"
                  hintKey="batchImportDropHint"
                  activeKey="batchImportDropActive"
                  hasFileKey="batchImportDropHasFile"
                  clearLabelKey="tenantCsvDropClear"
                  onFileSelect={processFile}
                  onClear={handleChangeFile}
                />
                <p className="text-sm" style={{ color: colors.text.secondary }}>
                  {t('batchImportFileHint')}
                </p>
                {parseError ? (
                  <p className="text-sm" style={{ color: colors.danger[600] }}>
                    {parseError}
                  </p>
                ) : null}
              </div>
            )}

            {step === 'map' && (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-medium truncate" style={{ color: colors.text.primary }}>
                    {fileLabel}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-lg border border-border-light p-1">
                      {['row', 'column'].map((value) => (
                        <button
                          key={value}
                          type="button"
                          disabled={importing}
                          onClick={() => handleOrientationChange(value)}
                          className={cn(
                            'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                            headerOrientation === value
                              ? 'bg-primary-500 text-primary-900'
                              : 'text-text-secondary hover:bg-background-tertiary'
                          )}
                        >
                          {value === 'row'
                            ? t('csvMapperOrientationRow')
                            : t('csvMapperOrientationColumn')}
                        </button>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleChangeFile}
                      disabled={importing}
                    >
                      {t('batchImportChangeFile')}
                    </Button>
                  </div>
                </div>

                <div>
                  <h3 className="mb-1 text-sm font-semibold" style={{ color: colors.text.primary }}>
                    {t('batchImportMapColumnsTitle')}
                  </h3>
                  <p className="mb-4 text-sm" style={{ color: colors.text.secondary }}>
                    {t('batchImportMapColumnsDescription')}
                  </p>
                  <CsvColumnConnector
                    headers={headers}
                    dtoFields={BATCH_IMPORT_FIELD_IDS}
                    mapping={mapping}
                    onMappingChange={setMapping}
                    disabled={importing}
                    fieldLabels={fieldLabels}
                    requiredFields={REQUIRED}
                  />
                  {!isMappingValid(mapping) ? (
                    <p className="mt-3 text-sm" style={{ color: colors.warning[600] }}>
                      {t('batchImportRequiredMapping')}
                    </p>
                  ) : null}
                </div>

                {isMappingValid(mapping) && previewCompanies.length > 0 ? (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold" style={{ color: colors.text.primary }}>
                      {t('batchImportPreviewTitle')}
                    </h3>
                    <p className="mb-2 text-xs" style={{ color: colors.text.tertiary }}>
                      {t('batchImportRowCount', { count: fullPayload.length, chunks: chunkCount })}
                    </p>
                    <div
                      className="overflow-x-auto rounded-lg border"
                      style={{ borderColor: colors.border.light }}
                    >
                      <table className="w-full text-left text-xs">
                        <thead>
                          <tr style={{ backgroundColor: colors.gray[50] }}>
                            <th className="px-2 py-2 font-medium" style={{ color: colors.text.secondary }}>
                              {t('batchImportFieldTicker')}
                            </th>
                            <th className="px-2 py-2 font-medium" style={{ color: colors.text.secondary }}>
                              {t('batchImportFieldName')}
                            </th>
                            <th className="px-2 py-2 font-medium" style={{ color: colors.text.secondary }}>
                              {t('batchImportFieldIndustry')}
                            </th>
                            <th className="px-2 py-2 font-medium" style={{ color: colors.text.secondary }}>
                              {t('batchImportFieldTier')}
                            </th>
                            <th className="px-2 py-2 font-medium" style={{ color: colors.text.secondary }}>
                              {t('batchImportFieldCountry')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewCompanies.map((c, idx) => (
                            <tr key={idx} style={{ borderTop: `1px solid ${colors.border.light}` }}>
                              <td className="px-2 py-2" style={{ color: colors.text.primary }}>
                                {c.ticker}
                              </td>
                              <td className="px-2 py-2" style={{ color: colors.text.primary }}>
                                {c.name}
                              </td>
                              <td className="px-2 py-2" style={{ color: colors.text.secondary }}>
                                {c.industry || '—'}
                              </td>
                              <td className="px-2 py-2" style={{ color: colors.text.secondary }}>
                                {c.tier || '—'}
                              </td>
                              <td className="px-2 py-2" style={{ color: colors.text.secondary }}>
                                {c.country || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    type="button"
                    variant="default"
                    onClick={handleImport}
                    disabled={!isMappingValid(mapping) || !fullPayload.length || importing}
                  >
                    {importing ? t('batchImportUploading') : t('batchImportSubmit')}
                  </Button>
                </div>
              </>
            )}

            {result ? (
              <div
                className="rounded-lg border p-4 space-y-2"
                style={{
                  borderColor: result.ok ? colors.success[200] : colors.danger[200],
                  backgroundColor: result.ok ? colors.success[50] : colors.danger[50],
                }}
              >
                {result.ok ? (
                  <p className="text-sm font-medium" style={{ color: colors.text.primary }}>
                    {t('batchImportSuccessSummaryV2', {
                      created: result.created ?? 0,
                      updated: result.updated ?? 0,
                      failed: result.failed ?? 0,
                    })}
                  </p>
                ) : (
                  <p className="text-sm font-medium" style={{ color: colors.danger[700] }}>
                    {result.message}
                  </p>
                )}
                {result.errors?.length > 0 ? (
                  <div>
                    <p className="mb-1 text-xs font-semibold" style={{ color: colors.text.secondary }}>
                      {t('batchImportFailureListTitle')}
                    </p>
                    <ul
                      className="max-h-32 list-disc space-y-1 overflow-y-auto pl-4 text-xs"
                      style={{ color: colors.text.primary }}
                    >
                      {result.errors.slice(0, 50).map((errorMessage, i) => (
                        <li key={i}>{errorMessage}</li>
                      ))}
                      {result.errors.length > 50 ? (
                        <li style={{ color: colors.text.tertiary }}>
                          {t('batchImportFailuresTruncated')}
                        </li>
                      ) : null}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BatchImportCompaniesModal;
